import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import type {ProductAnalysisRun} from '@ozon-ai/analysis/contracts';
import {Alert, AlertDescription} from '@/components/ui/alert.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {Empty, EmptyDescription, EmptyHeader, EmptyTitle} from '@/components/ui/empty.tsx';
import {Skeleton} from '@/components/ui/skeleton.tsx';
import {api} from '../../../api/client';
import {runStatusLabel} from '../../../components/product-analysis/productAnalysisLabels';
import {useProductPageContext} from './product-context';

export function ProductRunsPage() {
	const {offerId} = useProductPageContext();
	const [runs, setRuns] = useState<ProductAnalysisRun[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setRuns(null);
		setError(null);
		void api
			.get<ProductAnalysisRun[]>(`/product-analyses?offerId=${encodeURIComponent(offerId)}&limit=20`)
			.then((items) => {
				if (!cancelled) setRuns(items);
			})
			.catch((err: unknown) => {
				if (!cancelled) setError(err instanceof Error ? err.message : 'Не удалось загрузить анализы');
			});
		return () => {
			cancelled = true;
		};
	}, [offerId]);

	return (
		<section className="flex flex-col gap-3">
			<h2 className="text-base font-medium">Анализы</h2>
			{error ? (
				<Alert variant="destructive">
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			) : null}
			{runs == null && !error ? <Skeleton className="h-24 w-full"/> : null}
			{runs?.length === 0 ? (
				<Empty className="border border-border">
					<EmptyHeader>
						<EmptyTitle>Запусков ещё нет</EmptyTitle>
						<EmptyDescription>История ProductAnalysis для этого SKU пуста</EmptyDescription>
					</EmptyHeader>
				</Empty>
			) : null}
			{runs?.map((run) => (
				<div key={run.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border py-3">
					<div className="flex min-w-0 flex-col gap-1">
						<Link className="truncate underline" to={`/analyses/sku/${encodeURIComponent(offerId)}/runs/${encodeURIComponent(run.id)}`}>
							{run.periodFrom}–{run.periodTo}
						</Link>
						<p className="text-sm text-muted-foreground">{run.rulesVersion}</p>
					</div>
					<Badge variant="outline">{runStatusLabel(run.status)}</Badge>
				</div>
			))}
		</section>
	);
}
